import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Download } from "lucide-react";
import { API, axios, formatIcelandicNumber, BACKEND_URL } from "@/utils/constants";

export const DokobitDialog = ({
  open, onOpenChange,
  dokobitApp, dokobitSigners, setDokobitSigners,
  dokobitLoading, setDokobitLoading,
  signingUrls, setSigningUrls,
  fetchApplications,
  setIsElectronicSignature, setPdfApp, setPdfType,
}) => {
  const showDokobitDialog = open;
  const setShowDokobitDialog = onOpenChange;
  return (
<Dialog open={showDokobitDialog} onOpenChange={setShowDokobitDialog}>
  <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
    <DialogHeader>
      <DialogTitle>Senda í undirritun með Dokobit</DialogTitle>
    </DialogHeader>
    
    {dokobitApp && (
      <div className="space-y-4">
        {/* Loan Information */}
        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <h3 className="font-semibold text-sm mb-2">Upplýsingar um lán</h3>
          <div className="text-xs space-y-1">
            <div><strong>Lántaki:</strong> {dokobitApp.borrower_info?.full_name}</div>
            <div><strong>Tegund láns:</strong> {
              dokobitApp.loan_type === 'skuldabref' ? 'Skuldabréf' :
              dokobitApp.loan_type === 'vedskuldabref' ? 'Veðskuldabréf' :
              dokobitApp.loan_type === 'kaupleigusamning' ? 'Kaupleigusamningur' :
              dokobitApp.loan_type
            }</div>
            <div><strong>Lánsfjárhæð:</strong> {(dokobitApp.loan_amount || 0).toLocaleString('is-IS')} kr.</div>
            <div><strong>Lánstími:</strong> {dokobitApp.requested_term_months} mánuðir</div>
          </div>
        </div>

        {!signingUrls ? (
          <>
            {/* Signers Section */}
            <div>
              <h3 className="font-semibold text-sm mb-2">Undirritarar</h3>
              <div className="space-y-2">
                {dokobitSigners.map((signer, index) => (
                  <div key={index} className="border rounded p-3 bg-gray-50">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">Fornafn</Label>
                        <Input
                          value={signer.name}
                          onChange={(e) => {
                            const newSigners = [...dokobitSigners];
                            newSigners[index].name = e.target.value;
                            setDokobitSigners(newSigners);
                          }}
                          className="h-8 text-xs"
                          placeholder="Fornafn"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Eftirnafn</Label>
                        <Input
                          value={signer.surname}
                          onChange={(e) => {
                            const newSigners = [...dokobitSigners];
                            newSigners[index].surname = e.target.value;
                            setDokobitSigners(newSigners);
                          }}
                          className="h-8 text-xs"
                          placeholder="Eftirnafn"
                        />
                      </div>
                      <div className="col-span-2">
                        <Label className="text-xs">Kennitala</Label>
                        <Input
                          value={signer.code}
                          onChange={(e) => {
                            const newSigners = [...dokobitSigners];
                            newSigners[index].code = e.target.value;
                            setDokobitSigners(newSigners);
                          }}
                          className="h-8 text-xs"
                          placeholder="Kennitala (10 tölustafir)"
                          maxLength={10}
                        />
                      </div>
                    </div>
                    {dokobitSigners.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newSigners = dokobitSigners.filter((_, i) => i !== index);
                          setDokobitSigners(newSigners);
                        }}
                        className="mt-2 text-red-600 hover:text-red-700"
                      >
                        Fjarlægja
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setDokobitSigners([...dokobitSigners, {
                    name: '',
                    surname: '',
                    code: '',
                    country_code: 'is',
                    signing_purpose: 'signature'
                  }]);
                }}
                className="mt-2"
              >
                + Bæta við undirritara
              </Button>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => setShowDokobitDialog(false)}
                disabled={dokobitLoading}
              >
                Hætta við
              </Button>
              <Button
                onClick={() => {
                  // First open the PDF in electronic signature mode
                  setIsElectronicSignature(true);
                  setPdfApp(dokobitApp);
                  // Set pdfType based on loan_type
                  if (dokobitApp.loan_type === 'vedskuldabref') {
                    setPdfType('vedskuldabref');
                  } else if (dokobitApp.loan_type === 'kaupleigusamning') {
                    setPdfType('kaupleigusamningur');
                  } else {
                    setPdfType('skuldabref');
                  }
                  // Keep Dokobit dialog open for user to come back
                }}
                className="bg-green-600 hover:bg-green-700"
              >
                <Download className="h-4 w-4 mr-1" />
                Prenta til undirritunar
              </Button>
              <Button
                onClick={async () => {
                  setDokobitLoading(true);
                  try {
                    // Step 1: Initiate signing request
                    const documentType = 
                      dokobitApp.loan_type === 'vedskuldabref' ? 'Veðskuldabréf' :
                      dokobitApp.loan_type === 'kaupleigusamning' ? 'Kaupleigusamningur' :
                      'Skuldabréf';
                    
                    const initiateResponse = await axios.post(`${BACKEND_URL}/api/dokobit/initiate-signing`, {
                      loan_id: dokobitApp.id,
                      document_type: documentType,
                      signers: dokobitSigners,
                      postback_url: `${BACKEND_URL}/api/dokobit/webhook`
                    });
                    
                    if (!initiateResponse.data.success) {
                      throw new Error(initiateResponse.data.message || 'Failed to initiate signing');
                    }
                    
                    const signingId = initiateResponse.data.signing_token;
                    
                    // Step 2: Generate and upload PDF
                    toast({
                      title: "Að búa til PDF...",
                      description: "Vinsamlegast bíðið",
                    });
                    
                    // Generate PDF from the document
                    const pdfContent = document.getElementById('pdf-content');
                    if (!pdfContent) {
                      // Need to generate PDF first - open the dialog to trigger generation
                      throw new Error('PDF generation not available yet. Please open the document first.');
                    }
                    
                    // For now, show a message that PDF upload is needed
                    toast({
                      title: "Undirritunarbeiðni stofnuð",
                      description: "Kerfi er tilbúið til undirritunar. Athugið: PDF skjal þarf að hlaða upp handvirkt.",
                      variant: "default"
                    });
                    
                    // Show signing URLs (will be empty until upload completes)
                    setSigningUrls({
                      signing_id: signingId,
                      message: 'Vinsamlegast hlaðið upp PDF skjali til að klára uppsetninguna.',
                      upload_url: `${BACKEND_URL}/api/dokobit/upload-document/${signingId}`
                    });
                    
                  } catch (error) {
                    console.error('Error initiating Dokobit signing:', error);
                    toast({
                      title: "Villa",
                      description: error.response?.data?.detail || error.message || 'Ekki tókst að hefja undirritun',
                      variant: "destructive"
                    });
                  } finally {
                    setDokobitLoading(false);
                  }
                }}
                disabled={dokobitLoading || dokobitSigners.some(s => !s.name || !s.surname || !s.code)}
                className="bg-green-600 hover:bg-green-700"
              >
                {dokobitLoading ? 'Að vinna...' : 'Senda í undirritun'}
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Signing URLs Display */}
            <div className="space-y-3">
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <h3 className="font-semibold text-sm mb-2 text-green-800">✓ Undirritunarbeiðni send</h3>
                <p className="text-xs text-green-700">{signingUrls.message || 'Skjalið hefur verið sent til undirritunar.'}</p>
              </div>
              
              {signingUrls.signing_urls && Object.keys(signingUrls.signing_urls).length > 0 && (
                <div>
                  <h3 className="font-semibold text-sm mb-2">Tenglar fyrir undirritarar</h3>
                  <div className="space-y-2">
                    {Object.entries(signingUrls.signing_urls).map(([signerId, url]) => {
                      const signer = dokobitSigners.find((_, idx) => (idx + 1).toString() === signerId) || {};
                      return (
                        <div key={signerId} className="border rounded p-2 bg-gray-50">
                          <div className="text-xs font-semibold mb-1">
                            {signer.name} {signer.surname}
                          </div>
                          <div className="flex gap-2 items-center">
                            <Input
                              value={url}
                              readOnly
                              className="h-8 text-xs font-mono"
                            />
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                navigator.clipboard.writeText(url);
                                toast({
                                  title: "Afritað",
                                  description: "Tengill afritaður á klemmuspjald"
                                });
                              }}
                            >
                              Afrita
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => window.open(url, '_blank')}
                              className="bg-blue-600 hover:bg-blue-700"
                            >
                              Opna
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {signingUrls.signing_id && (
                <div className="text-xs text-gray-600">
                  <strong>Auðkenni undirritunar:</strong> {signingUrls.signing_id}
                </div>
              )}
              
              {signingUrls.upload_url && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                  <p className="text-xs text-yellow-800">
                    <strong>Athugið:</strong> PDF skjalið þarf að hlaða upp handvirkt til að ljúka uppsetningu.
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  setShowDokobitDialog(false);
                  setSigningUrls(null);
                }}
              >
                Loka
              </Button>
            </div>
          </>
        )}
      </div>
    )}
  </DialogContent>
</Dialog>

  );
};
